import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useNodes,
  Panel,
  useReactFlow,
  getBezierPath,
  BaseEdge,
  EdgeLabelRenderer,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import {
  Save, Play, ArrowLeft, Plus, Settings, Zap,
  Loader2, CheckCircle, XCircle, ChevronRight, ChevronLeft, Key, Globe, Sun, Moon, Copy, Sparkles, X,
  StopCircle, PlayCircle, RotateCcw,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { v4 as uuidv4 } from 'uuid';
import { buildCronExpression, TIMEZONE_MAP } from '../utils/CronUtils';

import TriggerNode from '../components/workflow/nodes/TriggerNode';
import ActionNode from '../components/workflow/nodes/ActionNode';
import ConditionNode from '../components/workflow/nodes/ConditionNode';
import AutonomousNode from '../components/workflow/nodes/AutonomousNode';
import NodeSidebar from '../components/workflow/NodeSidebar';
import NodeConfigPanel from '../components/workflow/NodeConfigPanel';
import ExecutionPanel from '../components/workflow/ExecutionPanel';
import CredentialManager from '../components/workflow/CredentialManager';
import AIWorkflowGenerator from '../components/workflow/AIWorkflowGenerator';

import {
  getWorkflow,
  createWorkflow,
  updateWorkflow,
  executeWorkflow,
  getNodeTypes,
  createWorkflowWS,
  validateCron, getJobInfo, pauseWorkflow, resumeWorkflow,
  cancelExecution, getWorkflowStats, cloneWorkflow, getRunHistory
} from '../lib/workflowApi';

// ── Custom Deletable Edge ──────────────────────────────────────────────────
function DeletableEdge({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd,
}) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });
  const { setEdges } = useReactFlow();

  const onEdgeClick = (evt, edgeId) => {
    evt.stopPropagation();
    setEdges((edges) => edges.filter((e) => e.id !== edgeId));
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <button
            onClick={(event) => onEdgeClick(event, id)}
            style={{
              width: 16,
              height: 16,
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 5px rgba(0,0,0,0.2)',
            }}
            title="Delete Connection"
          >
            <X size={10} />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

const EDGE_TYPES = { default: DeletableEdge };

// ── Custom node type registry ──────────────────────────────────────────────
const TRIGGER_TYPES = ['ManualTrigger', 'WebhookTrigger', 'ScheduleTrigger'];
const CONDITION_TYPES = ['IfCondition', 'SwitchCase'];
const AUTONOMOUS_TYPES = ['AutonomousNode'];

const NODE_TYPES = {
  trigger:    TriggerNode,
  action:     ActionNode,
  condition:  ConditionNode,
  autonomous: AutonomousNode,
};

// MiniMap custom node — renders coloured rect + label text in SVG
function MiniMapCustomNode({ x, y, width, height, color, id }) {
  const nodes = useNodes();
  const node = nodes.find((n) => n.id === id);
  const raw = node?.data?.label || node?.data?.type || '';
  const label = raw.length > 9 ? raw.slice(0, 8) + '…' : raw;
  // fontSize must be in flow-space units (not screen pixels).
  // The minimap scales flow coords down ~3-8x to fit 120px height,
  // so height*0.42 (e.g. 21 for a 50-unit node) → ~5-7px on screen.
  const fontSize = height * 0.42;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={color} rx={4} opacity={0.95} />
      <text
        x={x + width / 2}
        y={y + height / 2 + fontSize * 0.38}
        textAnchor="middle"
        fontSize={fontSize}
        fill="#ffffff"
        fontWeight="800"
        fontFamily="Inter, sans-serif"
        style={{ pointerEvents: 'none' }}
      >
        {label}
      </text>
    </g>
  );
}

const MINIMAP_NODE_COLORS = {
  ManualTrigger: '#10B981', WebhookTrigger: '#10B981', ScheduleTrigger: '#10B981',
  IfCondition: '#EF4444', SwitchCase: '#EF4444',
  OpenAI: '#8B5CF6', MarketplaceAgent: '#F87217', AITransform: '#8B5CF6',
  TextSummarizer: '#8B5CF6', SentimentAnalysis: '#8B5CF6',
  SendEmail: '#F59E0B', SlackNotification: '#F59E0B',
  PostgreSQLQuery: '#3B82F6', MySQLQuery: '#3B82F6',
  HttpRequest: '#06B6D4', WebScraper: '#06B6D4',
  SetVariable: '#64748B', JSONTransform: '#64748B',
  DelayTimer: '#64748B', CodeExecutor: '#64748B', DataValidator: '#64748B',
  AutonomousNode: '#F97316',
};

function getNodeTypeKey(type) {
  if (TRIGGER_TYPES.includes(type)) return 'trigger';
  if (CONDITION_TYPES.includes(type)) return 'condition';
  if (AUTONOMOUS_TYPES.includes(type)) return 'autonomous';
  return 'action';
}

// ── Helpers to convert between backend and ReactFlow formats ───────────────
function backendNodesToRF(nodes) {
  return nodes.map((n) => ({
    id: n.id,
    type: getNodeTypeKey(n.type),
    position: { x: n.position?.x ?? 100, y: n.position?.y ?? 100 },
    data: {
      type: n.type,
      label: n.label || n.type,
      config: n.config || {},
      input_mapping: n.input_mapping || {},
    },
  }));
}

function backendEdgesToRF(edges) {
  return edges.map((e) => ({
    id: e.id,
    source: e.source,
    sourceHandle: e.sourceHandle || null,
    target: e.target,
    targetHandle: e.targetHandle || null,
    style: { stroke: '#4A5568', strokeWidth: 2 },
    animated: false,
  }));
}

function rfNodesToBackend(nodes) {
  return nodes.map((n) => ({
    id: n.id,
    type: n.data.type,
    label: n.data.label,
    position: { x: n.position.x, y: n.position.y },
    config: n.data.config || {},
    input_mapping: n.data.input_mapping || {},
  }));
}

function rfEdgesToBackend(edges) {
  return edges.map((e) => ({
    id: e.id,
    source: e.source,
    sourceHandle: e.sourceHandle || null,
    target: e.target,
    targetHandle: e.targetHandle || null,
  }));
}

// ── Auto-layout using dagre ──────────────────────────────────────────────
// IMPORTANT: Create a fresh graph instance on every call.
// A module-level singleton would accumulate stale nodes across invocations.
const NODE_WIDTH = 220;
const NODE_HEIGHT = 90;

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  // Fresh graph per call — avoids stale node/edge state
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, nodesep: 60, ranksep: 80 });

  const isHorizontal = direction === 'LR';

  nodes.forEach((node) => {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    if (edge.source && edge.target) {
      g.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(g);

  return nodes.map((node) => {
    const pos = g.node(node.id);
    return {
      ...node,
      targetPosition: isHorizontal ? 'left' : 'top',
      sourcePosition: isHorizontal ? 'right' : 'bottom',
      // dagre positions are center-based; ReactFlow uses top-left
      position: {
        x: pos ? pos.x - NODE_WIDTH / 2 : 100,
        y: pos ? pos.y - NODE_HEIGHT / 2 : 100,
      },
    };
  });
};

// ── Inline Clone Modal ─────────────────────────────────────────────────────
function CloneModal({ defaultName, isDark, onConfirm, onClose }) {
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr(null);
    try { await onConfirm(name.trim()); }
    catch (ex) { setErr(ex?.response?.data?.detail || 'Clone failed'); setBusy(false); }
  };

  const bg     = isDark ? '#0d1117' : '#fff';
  const border = isDark ? '#1F2937' : '#e2e8f0';
  const text1  = isDark ? '#E6EDF3' : '#0f172a';
  const text2  = isDark ? '#64748B' : '#64748b';

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }}
      onClick={onClose}
    >
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16, width: '100%', maxWidth: 420, padding: 28, boxShadow: '0 24px 48px rgba(0,0,0,0.35)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Copy size={16} color="#8B5CF6" />
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: text1 }}>Clone Workflow</span>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: text2, display: 'flex', padding: 4 }}><X size={18} /></button>
        </div>
        <p style={{ fontSize: 13, color: text2, marginBottom: 18, lineHeight: 1.6 }}>
          All nodes and edges will be copied. Execution history is not transferred.
        </p>
        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: text2, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>New Name</label>
          <input
            ref={inputRef} value={name}
            onChange={e => { setName(e.target.value); setErr(null); }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 9, border: `1px solid ${err ? '#EF4444' : border}`, background: isDark ? '#0a0f18' : '#F8FAFC', color: text1, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
            placeholder="Workflow name…"
          />
          {err && <p style={{ color: '#EF4444', fontSize: 12, marginTop: 6 }}>{err}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={busy} style={{ padding: '9px 18px', borderRadius: 9, background: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9', border: `1px solid ${border}`, color: text2, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button type="submit" disabled={busy || !name.trim()} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 9, background: 'linear-gradient(135deg,#7C3AED,#6D28D9)', border: '1px solid rgba(139,92,246,0.5)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'inherit' }}>
              {busy ? <Loader2 size={14} style={{ animation: 'spin 0.9s linear infinite' }} /> : <Copy size={14} />}
              {busy ? 'Cloning…' : 'Clone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function WorkflowEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [savedId, setSavedId] = useState(null);
  const isNew = !savedId && (!id || id === 'new');
  const { isDark, toggleTheme } = useTheme();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [workflow, setWorkflow] = useState(null);
  const [workflowName, setWorkflowName] = useState('Untitled Workflow');
  const [triggerType, setTriggerType] = useState('manual');
  const [cronExpression, setCronExpression] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [catalogTypes, setCatalogTypes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
//  Added Webhook copy state 
  const [webhookCopied, setWebhookCopied] = useState(false);

  const [cronValid, setCronValid] = useState(null);       // null | true | false
  const [nextRunTime, setNextRunTime] = useState(null);   // ISO string from scheduler
  const [workflowStats, setWorkflowStats] = useState(null);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [editorToast, setEditorToast] = useState(null); // { msg, ok }

  const showEditorToast = useCallback((msg, ok = true) => {
    setEditorToast({ msg, ok, id: Date.now() });
    setTimeout(() => setEditorToast(null), 3200);
  }, []);

  // Keep selectedNode in sync with nodes array
  useEffect(() => {
    if (selectedNode) {
      const updated = nodes.find((n) => n.id === selectedNode.id);
      if (updated) setSelectedNode(updated);
    }
  }, [nodes]);

  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error'
  const [executing, setExecuting] = useState(false);
  const [stoppedExecution, setStoppedExecution] = useState(false); // flips Run→Restart after manual Stop
  const [currentExecution, setCurrentExecution] = useState(null);
  const [nodeExecutionStatus, setNodeExecutionStatus] = useState({});
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const reactFlowWrapperRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [execPanelOpen, setExecPanelOpen] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [aiPanelOpen, setAiPanelOpen] = useState(false);

  // Load workflow and node catalog
  useEffect(() => {
    async function load() {
      try {
        const [typesRes] = await Promise.all([getNodeTypes()]);
        setCatalogTypes(typesRes.data);

        if (id && id !== 'new') {
          const wfRes = await getWorkflow(id);
          const wf = wfRes.data;
          setWorkflow(wf);
          setWorkflowName(wf.name);
          setTriggerType(wf.trigger_type || 'manual');
          setCronExpression(wf.cron_expression || '');
          setIsActive(wf.is_active !== false);
          // Fetch scheduler job info + stats in parallel
          const [jobRes, statsRes] = await Promise.allSettled([
            getJobInfo(id),
            getWorkflowStats(id),
          ]);
          if (jobRes.status === 'fulfilled' && jobRes.value.data?.scheduled) {
            setNextRunTime(jobRes.value.data.next_run_time);
          }
          if (statsRes.status === 'fulfilled') {
            setWorkflowStats(statsRes.value.data);
}
          setNodes(backendNodesToRF(wf.nodes || []));
          setEdges(backendEdgesToRF(wf.edges || []));
        }
      } catch (err) {
        setError('Failed to load workflow');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  // Connect to workflow-level websocket for external triggers (e.g. webhooks)
  useEffect(() => {
    if (!id || id === 'new') return;
    
    const ws = createWorkflowWS(id);
    ws.onmessage = (ev) => {
      try {
        const event = JSON.parse(ev.data);
        if (event.type === 'execution_started' && event.trigger_type !== 'manual') {
          // An external execution started (schedule / webhook)! Pop open the panel.
          setExecPanelOpen(true);
          setCurrentExecution({
            status: 'started',
            workflow_id: id,
            id: event.execution_id,
            trigger_type: event.trigger_type,
          });
        }
      } catch (err) { /* ignore parse errors */ }
    };
    
    return () => {
      ws.close();
    };
  }, [id]);

  // Sync triggerType state with nodes on canvas (The Canvas is the Source of Truth)
  useEffect(() => {
    const triggerNode = nodes.find((n) => TRIGGER_TYPES.includes(n.data.type));
    if (triggerNode) {
      const type = triggerNode.data.type;
      if (type === 'WebhookTrigger') setTriggerType('webhook');
      else if (type === 'ScheduleTrigger') setTriggerType('schedule');
      else if (type === 'ManualTrigger') setTriggerType('manual');
    } else {
      // If no trigger node is on canvas, default to manual
    }
  }, [nodes]);

  // ── Auto-sync tool count for Autonomous nodes ──────────────────────────────
  // This ensures the badge updates whenever an edge is added or removed.
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.data?.type === 'AutonomousNode') {
          // Count outgoing edges from this node
          const count = edges.filter((e) => e.source === node.id).length;
          if (node.data.toolCount !== count) {
            return { ...node, data: { ...node.data, toolCount: count } };
          }
        }
        return node;
      })
    );
  }, [edges, setNodes]);

  // On connect
  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            id: uuidv4(),
            style: { stroke: '#4A5568', strokeWidth: 2 },
          },
          eds
        )
      ),
    [setEdges]
  );

  // Drop a node from sidebar onto canvas
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const typeData = e.dataTransfer.getData('application/reactflow');
      if (!typeData) return;

      const { type, label } = JSON.parse(typeData);

      // Use ref to get the exact ReactFlow canvas bounds
      // reactFlowInstance.project() converts viewport coords → flow coords
      if (!reactFlowInstance || !reactFlowWrapperRef.current) return;

      const bounds = reactFlowWrapperRef.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: e.clientX - bounds.left,
        y: e.clientY - bounds.top,
      });
      // Centre the node under the cursor
      position.x -= 100;
      position.y -= 20;

      const newNode = {
        id: uuidv4(),
        type: getNodeTypeKey(type),
        position,
        data: { type, label, config: {}, input_mapping: {} },
      };
      setNodes((nds) => {
        // Enforce Single Trigger: replace existing trigger nodes if a new one is dropped
        if (TRIGGER_TYPES.includes(type)) {
          // Collect IDs of the trigger nodes being evicted.
          const evictedIds = new Set(
            nds.filter((n) => TRIGGER_TYPES.includes(n.data.type)).map((n) => n.id)
          );
          // Remove all edges connected to the evicted trigger node(s).
          // Without this, the old edges remain as zombie edges in React state,
          // get saved to the DB, and cause the backend topological sort to see
          // an unreachable target node, throwing a false DAG cycle error.
          if (evictedIds.size > 0) {
            setEdges((eds) => eds.filter(
              (e) => !evictedIds.has(e.source) && !evictedIds.has(e.target)
            ));
          }
          return [...nds.filter((n) => !TRIGGER_TYPES.includes(n.data.type)), newNode];
        }
        return [...nds, newNode];
      });
    },
    [setNodes,setEdges,reactFlowInstance]  
  );

  // Select node to configure
  const onNodeClick = useCallback((e, node) => {
    e.stopPropagation();
    setSelectedNode(node);
    setConfigOpen(true);
  }, []);


  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setConfigOpen(false);
  }, []);




  // Update selected node config
  const updateNodeConfig = useCallback(
    (nodeId, config) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === nodeId) {
            const updated = { ...n, data: { ...n.data, config } };
            // Keep selectedNode in sync immediately
            setSelectedNode((sel) => (sel?.id === nodeId ? updated : sel));
            return updated;
          }
          return n;
        })
      );
      setNodes((nds) => {
        const targetNode = nds.find(n => n.id === nodeId);
        if (targetNode && targetNode.data.type === 'SwitchCase') {
          // Parse handles from new config
          const raw = config?.cases;
          let parsed = raw;
          if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed.startsWith('[')) {
              try { parsed = JSON.parse(trimmed); } catch { parsed = []; }
            } else {
              parsed = trimmed.split(',').map(s => s.trim()).filter(Boolean);
            }
          }
          const handles = Array.isArray(parsed)
            ? parsed.map(item => typeof item === 'object' && item !== null ? String(item.handle || item.case || '') : String(item)).filter(Boolean)
            : ['case_a', 'case_b'];

          const newHandles = [...handles.filter(h => h.toLowerCase() !== 'default'), 'default'];

          // Remove dangling edges whose sourceHandle is no longer present in newHandles
          setEdges((eds) => eds.filter(edge => {
            if (edge.source === nodeId) {
              return newHandles.includes(edge.sourceHandle);
            }
            return true;
          }));
        }
        return nds;
      });
    },
    [setNodes, setEdges]
  );
  

  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  const webhookUrl = !isNew && workflow?.id
    ? `${baseUrl}/api/webhook/${workflow?.id}`
    : '';
  const webhookSecret = workflow?.webhook_secret || '';

  const [webhookOpen, setWebhookOpen] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const webhookDropRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!webhookOpen) return;
    const handler = (e) => {
      if (webhookDropRef.current && !webhookDropRef.current.contains(e.target)) {
        setWebhookOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [webhookOpen]);

  const handleCopyWebhook = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl);
      setWebhookCopied(true);
      setTimeout(() => setWebhookCopied(false), 2000);
    }
  };

  const handleCopySecret = () => {
    if (webhookSecret) {
      navigator.clipboard.writeText(webhookSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  const handleCronBlur = async () => {
  if (!cronExpression) return setCronValid(null);
  try {
    await validateCron(cronExpression);
    setCronValid(true);
    if (!isNew) {
      const res = await getJobInfo(id);
      if (res.data?.scheduled) setNextRunTime(res.data.next_run_time);
    }
  } catch {
    setCronValid(false);
  }
};

  const handlePauseResume = async () => {
    try {
      // updateWorkflow persists is_active to DB and also syncs the APScheduler
      // cron job (add/remove) — works for manual, webhook, and scheduled workflows.
      await updateWorkflow(id, { is_active: !isActive });
      setIsActive((v) => !v);
      showEditorToast(isActive ? 'Workflow disabled' : 'Workflow enabled', true);
    } catch {
      showEditorToast('Failed to update workflow', false);
    }
  };

  // Stop the currently-running execution. The Run button flips to Restart after this.
  const handleStop = async () => {
    // Backend returns execution_id (not id) — support both field names
    const execId = currentExecution?.execution_id || currentExecution?.id;
    if (!execId) return;
    try {
      await cancelExecution(id, execId);
      setExecuting(false);
      setStoppedExecution(true);
      setCurrentExecution((prev) => ({ ...prev, status: 'cancelled' }));
      showEditorToast('Execution stopped', true);
    } catch {
      showEditorToast('Failed to stop execution', false);
    }
  };

  const handleClone = async (newName) => {
    try {
      const res = await cloneWorkflow(id, newName);
      setCloneModalOpen(false);
      navigate(`/workflow/${res.data.id}`);
    } catch (ex) {
      throw ex; // bubble to modal for inline error display
    }
  };

  // Delete a node
  const deleteNode = useCallback(
    (nodeId) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      if (selectedNode?.id === nodeId) {
        setSelectedNode(null);
        setConfigOpen(false);
      }
    },
    [setNodes, setEdges, selectedNode]
  );

  // Save workflow
  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {


      let generatedCron = cronExpression;
      let ownerTimezone = "UTC";

      if (triggerType === "schedule") {
        const scheduleNode = nodes.find(
          (n) => n.data.type === "ScheduleTrigger"
        );

        if (scheduleNode) {
          const config = scheduleNode.data.config || {};

          generatedCron = buildCronExpression(
            config.frequency || "daily",
            config.execution_time || "09:00",
            config.timezone || "UTC"
          );

          ownerTimezone =TIMEZONE_MAP[config.timezone] || "UTC";

          console.log("Schedule Config:", config);
          console.log("Generated Cron:", generatedCron);
          console.log("Owner Timezone:", ownerTimezone);
        }
      }
      const payload = {
        name: workflowName,
        trigger_type: triggerType,
        cron_expression: generatedCron || null,
        owner_timezone: ownerTimezone,
        is_active: isActive,
        nodes: rfNodesToBackend(nodes),
        edges: rfEdgesToBackend(edges),
      };

      if (isNew) {
        const res = await createWorkflow(payload);
        const newId = res.data.id;
        setSavedId(newId);  
        setWorkflow(res.data);  //  hydrate workflow state immediately

        // Fetch stats + job info right after creation
        const [jobRes, statsRes] = await Promise.allSettled([
          getJobInfo(newId),
          getWorkflowStats(newId),
        ]);
        if (jobRes.status === 'fulfilled' && jobRes.value.data?.scheduled) {
          setNextRunTime(jobRes.value.data.next_run_time);
        }
        if (statsRes.status === 'fulfilled') {
          setWorkflowStats(statsRes.value.data);
        }

        setSaveStatus('success');
        setTimeout(() => navigate(`/workflow/${newId}`, { replace: true }), 800);
      } else {
        await updateWorkflow(id, payload);
        const res = await updateWorkflow(id, payload);
        setWorkflow(res.data);
        setSaveStatus('success');
      }
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus(null), 2500);
    }
  };

  // Execute workflow
  const handleExecute = async () => {
    if (isNew) {
      alert('Save the workflow first before running it.');
      return;
    }
    setExecuting(true);
    setStoppedExecution(false); // clear Restart state on every fresh run
    setExecPanelOpen(true);
    setNodeExecutionStatus({});
    try {
      const res = await executeWorkflow(id, {});
      // Backend now pre-creates the execution record and returns its id.
      // Pass it through so ExecutionPanel can subscribe to the WebSocket
      // channel immediately — before the Celery worker even starts.
      setCurrentExecution({ status: 'queued', workflow_id: id, ...res.data });
    } catch (err) {
      setCurrentExecution({ status: 'failed', error: 'Failed to start execution' });
      setExecuting(false);
    }
  };

  // Update node visuals from execution events
  const handleExecutionEvent = useCallback((event) => {
    if (['execution_completed', 'execution_failed', 'execution_cancelled'].includes(event.type)) {
      setExecuting(false);
      // Success → back to Run; failure/cancel → show Restart button
      setStoppedExecution(event.type !== 'execution_completed');
    } else if (event.type === 'node_started') {
      setNodeExecutionStatus((prev) => ({ ...prev, [event.node_id]: 'running' }));
      setNodes((nds) =>
        nds.map((n) =>
          n.id === event.node_id
            ? { ...n, data: { ...n.data, executionStatus: 'running' } }
            : n
        )
      );
    } else if (event.type === 'node_completed') {
      setNodeExecutionStatus((prev) => ({ ...prev, [event.node_id]: 'success' }));
      setNodes((nds) =>
        nds.map((n) =>
          n.id === event.node_id
            ? { ...n, data: { ...n.data, executionStatus: 'success' } }
            : n
        )
      );
    } else if (event.type === 'node_failed') {
      setNodeExecutionStatus((prev) => ({ ...prev, [event.node_id]: 'failed' }));
      setNodes((nds) =>
        nds.map((n) =>
          n.id === event.node_id
            ? { ...n, data: { ...n.data, executionStatus: 'failed' } }
            : n
        )
      );
    } else if (event.type === 'node_skipped') {
      setNodeExecutionStatus((prev) => ({ ...prev, [event.node_id]: 'skipped' }));
      setNodes((nds) =>
        nds.map((n) =>
          n.id === event.node_id
            ? { ...n, data: { ...n.data, executionStatus: 'skipped' } }
            : n
        )
      );
    }
  }, [setNodes]);

  /**
   * Called by AIWorkflowGenerator when generation succeeds.
   * The API now returns pre-parsed nodes/edges — no YAML parsing needed.
   * We convert them to ReactFlow format and apply dagre auto-layout.
   */
  const onAIGenerate = useCallback((data) => {
    if (!data || !data.success) return;

    // ── 1. Set workflow metadata ──────────────────────────────────────────
    if (data.workflow_name) setWorkflowName(data.workflow_name);
    if (data.trigger_type) setTriggerType(data.trigger_type);
    if (data.cron_expression) setCronExpression(data.cron_expression);

    // ── 2. Convert pre-parsed nodes → ReactFlow nodes ─────────────────────
    // data.nodes already has `type` = ReactFlow key ('trigger'/'action'/'condition')
    // and `node_type` = platform type ('OpenAI', 'SlackNotification', ...)
    const newNodes = (data.nodes || []).map((n) => ({
      id: n.id,
      type: n.type,                       // ReactFlow component key
      position: { x: 0, y: 0 },          // dagre will set real position
      data: {
        type: n.node_type,                // platform type
        label: n.label || n.node_type,
        config: n.config || {},
        input_mapping: n.input_mapping || {},
      },
    }));

    // ── 3. Convert pre-parsed edges → ReactFlow edges ─────────────────────
    const newEdges = (data.edges || []).map((e) => ({
      id: e.id || uuidv4(),
      source: e.source,
      sourceHandle: e.sourceHandle || null,
      target: e.target,
      targetHandle: e.targetHandle || null,
      style: { stroke: '#4A5568', strokeWidth: 2 },
      animated: false,
    }));

    // ── 4. Apply dagre auto-layout ─────────────────────────────────────────
    const layoutedNodes = getLayoutedElements(newNodes, newEdges);

    setNodes(layoutedNodes);
    setEdges(newEdges);
  }, [setNodes, setEdges]);

  // Clear execution visuals
  const clearExecution = useCallback(() => {
    setNodeExecutionStatus({});
    setNodes((nds) =>
      nds.map((n) => {
        const { executionStatus, ...rest } = n.data;
        return { ...n, data: rest };
      })
    );
  }, [setNodes]);

  if (loading) {
    return (
      <div style={{ ...styles.loadingContainer, background: isDark ? '#080d14' : '#F0F4F8' }}>
        <Loader2 size={36} color="#F87217" className="spin" />
        <p style={{ color: isDark ? '#94A3B8' : '#64748b', marginTop: 16 }}>Loading workflow…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.loadingContainer, background: isDark ? '#080d14' : '#F0F4F8' }}>
        <XCircle size={36} color="#EF4444" />
        <p style={{ color: isDark ? '#94A3B8' : '#64748b', marginTop: 16 }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ ...styles.root, background: isDark ? '#060B14' : '#F0F4F8', position: 'relative', overflow: 'hidden' }}>
      {/* Animated grid */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
        backgroundImage: isDark
          ? 'linear-gradient(rgba(139,92,246,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.08) 1px, transparent 1px)'
          : 'linear-gradient(rgba(99,102,241,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.07) 1px, transparent 1px)',
        backgroundSize: '30px 30px',
        animation: 'gridScroll 16s linear infinite',
      }} />
      {/* Glow blobs */}
      <div style={{ position: 'absolute', top: '-15%', left: '5%', width: '50vw', height: '50vh', background: 'radial-gradient(ellipse, rgba(139,92,246,0.1) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'absolute', bottom: 0, right: 0, width: '40vw', height: '45vh', background: 'radial-gradient(ellipse, rgba(248,114,23,0.07) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />

      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      <div data-tour="editor-topbar" style={{ ...styles.topBar, background: isDark ? 'rgba(13,17,26,0.92)' : 'rgba(255,255,255,0.92)', borderBottom: `1px solid ${isDark ? '#1F2937' : 'rgba(0,0,0,0.08)'}`, backdropFilter: 'blur(12px)', position: 'relative', zIndex: 10 }}>
        <div style={styles.topLeft}>
          <button style={{ ...styles.backBtn, border: `1px solid ${isDark ? '#1F2937' : 'rgba(0,0,0,0.1)'}`, color: isDark ? '#94A3B8' : '#64748B' }} onClick={() => navigate('/workflow')}>
            <ArrowLeft size={16} />
          </button>
          <div style={{ ...styles.namePill, background: isDark ? '#161B22' : '#ffffff', border: `1px solid ${isDark ? '#30363D' : '#cbd5e1'}` }}>
            <Zap size={14} color="#F87217" />
            <input
              style={{ ...styles.nameInput, color: isDark ? '#E6EDF3' : '#0f172a', caretColor: isDark ? '#E6EDF3' : '#0f172a' }}
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              placeholder="Workflow name…"
            />
          </div>
          <select
            style={{ ...styles.triggerSelect, background: isDark ? '#161B22' : '#ffffff', border: `1px solid ${isDark ? '#30363D' : '#cbd5e1'}`, color: isDark ? '#94A3B8' : '#475569' }}
            value={triggerType}
            onChange={(e) => setTriggerType(e.target.value)}
            disabled={nodes.some((n) => TRIGGER_TYPES.includes(n.data.type))}
            title={nodes.some((n) => TRIGGER_TYPES.includes(n.data.type)) ? "Trigger type is controlled by the node on canvas" : ""}
          >
            <option value="manual">Manual Trigger</option>
            <option value="webhook">Webhook Trigger</option>
            <option value="schedule">Schedule (Cron)</option>
          </select>
          
          {triggerType === 'webhook' && (
            <div ref={webhookDropRef} style={{ position: 'relative', marginLeft: 8, flexShrink: 0 }}>
              {/* Trigger pill — shows short URL + dropdown toggle */}
              <div
                onClick={() => setWebhookOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: '#161B22', border: '1px solid #30363D',
                  borderRadius: 8, padding: '5px 12px',
                  cursor: 'pointer', userSelect: 'none',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#444c56'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#30363D'}
              >
                <Globe size={14} color="#94A3B8" />
                <span style={{ color: '#E6EDF3', fontSize: 12, fontWeight: 500 }}>Webhook</span>
                <span style={{ color: '#64748B', fontSize: 10 }}>{webhookOpen ? '▲' : '▼'}</span>
              </div>

              {/* Dropdown popover */}
              {webhookOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 10px)', left: 0,
                  background: '#0d1117', border: '1px solid #30363D',
                  borderRadius: 12, padding: '16px 20px', zIndex: 9999,
                  boxShadow: '0 12px 48px rgba(0,0,0,0.7)',
                  display: 'flex', flexDirection: 'column', gap: 16,
                  width: 420,
                }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #30363D', paddingBottom: 10 }}>
                    <Zap size={14} color="#F59E0B" fill="#F59E0B" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#E6EDF3' }}>Webhook Configuration</span>
                  </div>

                  {/* Show URL + Secret only after the first save.
                      Before saving, webhookSecret is null (generated by the backend).
                      We show a prompt instead of hiding the panel, so the pill stays
                      visible after switching from another trigger type. */}
                  {webhookSecret ? (
                    <>
                      {/* URL Row */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8' }}>ENDPOINT URL</span>
                          <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10B981', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                            onClick={handleCopyWebhook}
                          >
                            {webhookCopied ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy URL</>}
                          </button>
                        </div>
                        <div style={{ background: '#161B22', border: '1px solid #30363D', borderRadius: 8, padding: '8px 12px', wordBreak: 'break-all', fontSize: 12, color: '#E6EDF3', fontFamily: 'monospace', lineHeight: 1.4 }}>
                          {webhookUrl}
                        </div>
                      </div>

                      {/* Secret Row */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#F59E0B' }}>AUTH SECRET (AUTHORIZATION HEADER)</span>
                          <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F59E0B', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                            onClick={handleCopySecret}
                          >
                            {secretCopied ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy Secret</>}
                          </button>
                        </div>
                        <div style={{ background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#F59E0B', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                          {webhookSecret}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748B', background: '#161B22', padding: 8, borderRadius: 6, border: '1px dashed #30363D' }}>
                          <strong>Usage:</strong> Add header <code style={{ color: '#E6EDF3' }}>Authorization: Bearer {webhookSecret.slice(0, 8)}...</code> to your POST request.
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                      <span style={{ fontSize: 28 }}>&#128279;</span>
                      <span style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', lineHeight: 1.5 }}>
                        Save the workflow to generate your webhook URL and secret.
                      </span>
                      <span style={{ fontSize: 11, color: '#4B5563', textAlign: 'center' }}>
                        A unique endpoint URL and authorization secret will be created automatically.
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
        </div>

        <div style={styles.topRight}>
         
          {workflowStats && workflowStats.total_executions > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: isDark ? '#161B22' : '#F1F5F9',
              border: `1px solid ${isDark ? '#30363D' : '#cbd5e1'}`,
              borderRadius: 8, padding: '4px 10px', fontSize: 12,
              opacity: executing ? 0.4 : 1,
            }}>
              <span style={{ color: '#10B981', fontWeight: 600 }}>
                {workflowStats.success_rate}%
              </span>
              <span style={{ color: isDark ? '#64748B' : '#94A3B8' }}>
                ({workflowStats.total_executions} runs)
              </span>
            </div>
          )}

          {/* Theme toggle */}
          <button
            style={{ ...styles.iconBtn, background: isDark ? '#161B22' : '#F1F5F9', border: `1px solid ${isDark ? '#30363D' : 'rgba(0,0,0,0.1)'}`, color: isDark ? '#94A3B8' : '#64748B' }}
            onClick={toggleTheme}
            title={isDark ? 'Light mode' : 'Dark mode'}
          >
            {isDark ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          {/* Execution Monitor toggle */}
          <button
            style={{
              ...styles.iconBtn,
              background: execPanelOpen
                ? (isDark ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.12)')
                : (isDark ? '#161B22' : '#F1F5F9'),
              border: `1px solid ${execPanelOpen ? 'rgba(99,102,241,0.5)' : (isDark ? '#30363D' : 'rgba(0,0,0,0.1)')}`,
              color: execPanelOpen ? '#6366F1' : (isDark ? '#94A3B8' : '#64748B'),
              display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
            }}
            onClick={() => setExecPanelOpen((v) => !v)}
            title="Execution Monitor"
          >
            <Settings size={15} /> Executions
          </button>

          {/* Disable / Enable — workflow lifecycle (persists is_active in DB) */}
          {!isNew && !executing && (
            <button
              title={isActive ? 'Disable workflow (deactivates all future runs)' : 'Enable workflow (reactivates)'}
              style={{
                ...styles.iconBtn,
                display: 'flex', alignItems: 'center', gap: 5,
                background: isActive
                  ? (isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.1)')
                  : (isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.1)'),
                border: `1px solid ${isActive ? 'rgba(245,158,11,0.4)' : 'rgba(16,185,129,0.4)'}`,
                color: isActive ? '#F59E0B' : '#10B981',
                fontSize: 12, fontWeight: 700,
              }}
              onClick={handlePauseResume}
            >
              {isActive ? <StopCircle size={14} /> : <PlayCircle size={14} />}
              {isActive ? 'Disable' : 'Enable'}
            </button>
          )}

          {/* Clone — hidden while executing to reduce clutter */}
          {!isNew && !executing && (
            <button
              style={{
                ...styles.iconBtn,
                background: isDark ? '#161B22' : '#F1F5F9',
                border: `1px solid ${isDark ? '#30363D' : 'rgba(0,0,0,0.1)'}`,
                color: isDark ? '#94A3B8' : '#64748B',
                display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
              }}
              onClick={() => setCloneModalOpen(true)}
              title="Clone workflow"
            >
              <Copy size={14} /> Clone
            </button>
          )}

          {/* Save — disabled while executing */}
          <button
            data-tour="editor-save-btn"
            style={{
              ...styles.saveBtn,
              opacity: saving || executing ? 0.4 : 1,
              pointerEvents: executing ? 'none' : 'auto',
              background: saveStatus === 'error' ? '#EF4444' : (isDark ? '#1F2937' : '#e2e8f0'),
              borderColor: saveStatus === 'success' ? '#10B981' : (isDark ? '#374151' : '#cbd5e1'),
              color: isDark ? '#E6EDF3' : '#334155',
            }}
            onClick={handleSave}
            disabled={saving || executing}
          >
            {saving ? (
              <Loader2 size={15} className="spin" />
            ) : saveStatus === 'success' ? (
              <CheckCircle size={15} color="#10B981" />
            ) : (
              <Save size={15} />
            )}
            {saving ? 'Saving…' : saveStatus === 'success' ? 'Saved' : 'Save'}
          </button>

          {/* AI Generator — disabled while executing */}
          {!executing && (
            <button
              data-tour="editor-ai-btn"
              style={{
                ...styles.runBtn,
                background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.25)',
                border: 'none',
                marginLeft: 8,
                padding: '6px 14px',
              }}
              onClick={() => setAiPanelOpen(true)}
            >
              <Sparkles size={15} />
              AI Generate
            </button>
          )}

          {/* ── Run / Stop / Restart — single contextual button (n8n pattern) ── */}
          {executing ? (
            // RUNNING → show Stop (cancels current execution)
            <button
              data-tour="editor-run-btn"
              style={{
                ...styles.runBtn,
                marginLeft: 8,
                background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                boxShadow: '0 2px 10px rgba(239,68,68,0.45)',
                animation: 'pulse 1.8s ease-in-out infinite',
              }}
              onClick={handleStop}
              title="Stop the running execution"
            >
              <StopCircle size={15} /> Stop
            </button>
          ) : stoppedExecution ? (
            // STOPPED/FAILED → show Restart (re-runs from scratch)
            <button
              data-tour="editor-run-btn"
              style={{
                ...styles.runBtn,
                marginLeft: 8,
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                boxShadow: '0 2px 10px rgba(245,158,11,0.4)',
              }}
              onClick={handleExecute}
              title="Re-run the workflow from scratch"
            >
              <RotateCcw size={15} /> Restart
            </button>
          ) : (
            // IDLE → show Run
            <button
              data-tour="editor-run-btn"
              style={{ ...styles.runBtn, marginLeft: 8 }}
              onClick={handleExecute}
            >
              <Play size={15} /> Run
            </button>
          )}
        </div>
      </div>

      {/* ── Canvas Area ─────────────────────────────────────────────────── */}
      <div style={{ ...styles.canvasArea, position: 'relative', zIndex: 1 }}>
        {/* Node Sidebar */}
        {sidebarOpen && (
        <div data-tour="editor-node-sidebar">
          <NodeSidebar
            nodeTypes={catalogTypes}
            onClose={() => setSidebarOpen(false)}
          />
        </div>
        )}
        {!sidebarOpen && (
          
          <button
            style={styles.sidebarToggleBtn}
            onClick={() => setSidebarOpen(true)}
          >
            <Plus size={16} />
            <ChevronRight size={12} />
          </button>
        )}

        {/* React Flow Canvas */}
        <div data-tour="editor-canvas" ref={reactFlowWrapperRef} style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onInit={setReactFlowInstance}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            deleteKeyCode="Delete"
            style={{ background: 'transparent' }}
          >
            <Background color={isDark ? '#1F2937' : '#cbd5e1'} gap={16} />
            <Controls
              style={{
                background: isDark ? '#0d1117' : '#ffffff',
                border: `1px solid ${isDark ? '#1F2937' : '#e2e8f0'}`,
                borderRadius: 8,
              }}
            />
            <MiniMap
              nodeComponent={MiniMapCustomNode}
              nodeColor={(n) => MINIMAP_NODE_COLORS[n.data?.type] || '#8B5CF6'}
              pannable
              zoomable
              style={{
                backgroundColor: isDark ? '#080d14' : '#EEF2F7',
                border: `1.5px solid ${isDark ? '#374151' : '#cbd5e1'}`,
                borderRadius: 10,
                height: 120,
                boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.5)' : '0 4px 16px rgba(0,0,0,0.08)',
              }}
              maskColor={isDark ? 'rgba(8,13,20,0.7)' : 'rgba(238,242,247,0.7)'}
              position="bottom-left"
            />
            <Panel position="bottom-center">
              <div style={{ ...styles.hintBar, background: isDark ? 'rgba(13,17,23,0.85)' : 'rgba(255,255,255,0.9)', border: `1px solid ${isDark ? '#1F2937' : 'rgba(0,0,0,0.1)'}`, color: isDark ? '#64748B' : '#475569' }}>
                Drag nodes from the left panel · Click a node to configure ·
                Delete key to remove selected
              </div>
            </Panel>
          </ReactFlow>
        </div>

        {/* AI Panel */}
        <AnimatePresence>
          {aiPanelOpen && (
            <AIWorkflowGenerator
              isDark={isDark}
              onClose={() => setAiPanelOpen(false)}
              onGenerate={onAIGenerate}
            />
          )}
        </AnimatePresence>

        {/* Node Config Panel */}
        {configOpen && selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            catalogTypes={catalogTypes}
            onUpdate={updateNodeConfig}
            onDelete={deleteNode}
            onClose={() => setConfigOpen(false)}
            onManageCredentials={() => setShowCredentials(true)}
            workflowId={id}
            allNodes={nodes}
            allEdges={edges}
          />
        )}

        {/* Execution Panel */}
        {execPanelOpen && (
          <ExecutionPanel
            workflowId={id}
            currentExecution={currentExecution}
            onEvent={handleExecutionEvent}
            onClear={clearExecution}
            onClose={() => setExecPanelOpen(false)}
            onNewExecution={(exec) => {
              // Triggered when ExecutionPanel resumes from a node
              setCurrentExecution({ status: 'pending', workflow_id: id, ...exec });
            }}
          />
        )}

        {/* Credentials Modal */}
        {showCredentials && (
          <CredentialManager onClose={() => setShowCredentials(false)} />
        )}

        {/* Clone Modal */}
        {cloneModalOpen && (
          <CloneModal
            defaultName={`${workflowName} (copy)`}
            isDark={isDark}
            onConfirm={handleClone}
            onClose={() => setCloneModalOpen(false)}
          />
        )}

        {/* Editor toast */}
        {editorToast && (
          <div style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            zIndex: 99999, display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 10,
            background: editorToast.ok ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
            border: `1px solid ${editorToast.ok ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
            backdropFilter: 'blur(12px)',
            boxShadow: `0 8px 24px ${editorToast.ok ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
          }}>
            {editorToast.ok
              ? <CheckCircle size={14} color="#10B981" />
              : <XCircle size={14} color="#EF4444" />}
            <span style={{ fontSize: 13, fontWeight: 600, color: editorToast.ok ? '#10B981' : '#EF4444' }}>
              {editorToast.msg}
            </span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes gridScroll { from { background-position: 0 0; } to { background-position: 30px 30px; } }
        @keyframes pulse { 0%,100% { box-shadow: 0 2px 10px rgba(239,68,68,0.45); } 50% { box-shadow: 0 2px 20px rgba(239,68,68,0.8); } }
        .spin { animation: spin 0.9s linear infinite; }
        .react-flow__node { cursor: default !important; }
      `}</style>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#080d14',
    fontFamily: "'Inter', sans-serif",
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#080d14',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    height: 54,
    background: '#0d1117',
    borderBottom: '1px solid #1F2937',
    flexShrink: 0,
    gap: 12,
    zIndex: 10,
  },
  topLeft: { display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 },
  topRight: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' },
  backBtn: {
    background: 'transparent',
    border: '1px solid #1F2937',
    borderRadius: 8,
    padding: '6px 8px',
    color: '#94A3B8',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  namePill: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#161B22',
    border: '1px solid #30363D',
    borderRadius: 8,
    padding: '4px 10px',
  },
  nameInput: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#E6EDF3',
    fontSize: 14,
    fontWeight: 600,
    width: 200,
  },
  triggerSelect: {
    background: '#161B22',
    border: '1px solid #30363D',
    borderRadius: 8,
    padding: '5px 10px',
    color: '#94A3B8',
    fontSize: 13,
    outline: 'none',
    cursor: 'pointer',
  },
  webhookPill: {
    display: 'flex',
    alignItems: 'center',
    gap:4,
    background: '#161B22',
    border: '1px solid #30363D',
    borderRadius: 8,
    padding: '4px 6px 4px 10px',
    marginLeft: 8,
    minWidth:0,
    flexShrink:1,
  },
  webhookInput: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#E6EDF3',
    fontSize: 12,
    width: 'auto',
    maxWidth: 300,
    flex: 1,
    minWidth: 80,
    textOverflow: 'ellipsis',
  },
  iconBtnSmall: {
    background: 'transparent',
    border: 'none',
    padding: '4px',
    color: '#94A3B8',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  toggleLabel: { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' },
  toggleTrack: {
    position: 'relative',
    width: 36,
    height: 20,
    borderRadius: 10,
    transition: 'background 0.2s',
    cursor: 'pointer',
  },
  toggleThumb: {
    position: 'absolute',
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#fff',
    top: 2,
    transition: 'left 0.2s',
  },
  iconBtn: {
    background: '#161B22',
    border: '1px solid #30363D',
    borderRadius: 8,
    padding: '6px 9px',
    color: '#94A3B8',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    border: '1px solid #374151',
    borderRadius: 8,
    color: '#E6EDF3',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.18s',
  },
  runBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    background: 'linear-gradient(135deg, #F87217, #EA580C)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 10px rgba(248, 114, 23, 0.4)',
    transition: 'all 0.18s',
  },
  canvasArea: { display: 'flex', flex: 1, overflow: 'hidden' },
  sidebarToggleBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '12px 8px',
    background: 'rgba(13,17,23,0.6)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderLeft: 'none',
    color: '#94A3B8',
    cursor: 'pointer',
    borderRadius: '0 8px 8px 0',
    alignSelf: 'center',
  },
  hintBar: {
    padding: '4px 14px',
    fontSize: 11,
    backdropFilter: 'blur(6px)',
    borderRadius: 8,
  },
};